import React, { createContext, useState, useMemo, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeClassNames from 'rehype-class-names';
import { Button, ButtonWithIcon } from '@els/els-react--button';
import { reactions } from '@Constants';
import {
  focusAndScrollReferenceTarget,
  getResultReferenceTargetByResultId,
  RESULT_REFERENCE_TARGETS,
} from '@Components/Results/resultListIds';
import {
  extractRefsFromSummary,
  extractCompositeRefsFromSummary,
  findReactionForConditionToken,
  findReactionForIdToken,
} from '@Containers/Summary/summaryRefsUtil';
import { ASSISTANT_FEATURES, trackAssistantFeatureClick } from '@Containers/Summary/analytics';
import { revealReaction, revealReactionCondition } from '@Containers/Summary/reactionCitationCoordinator';
import ThumbsUpDown, { FEEDBACK_STATES } from './ThumbsUpDown';
import SummaryActionTooltip from './SummaryActionTooltip';
import SummaryDisclosureBanner from './SummaryDisclosureBanner';

const REF_LINK_TAG_NAME = 'cite';

const RefLinkContext = createContext({
  refsFromSummary: {},
  setOpenedReference: () => {},
  isStale: false,
});

const mdClassNamesMapping = {
  table: 'table c-els-table c-els-table--highlight-header c-els-table--vertical-dividers c-els-table--subtle-dividers',
  thead: 'c-els-table__head',
  tbody: 'c-els-table__body',
  tr: 'c-els-table__row',
  'thead tr': 'c-els-table__row c-els-table__row--head',
  th: 'c-els-table__cell c-els-table__cell--header',
  td: 'c-els-table__cell',
  a: 'c-els-link c-els-link--inline',
  blockquote: 'c-els-blockquote-inner c-els-blockquote--left-border c-els-blockquote--filled',
  h1: 'u-els-font-size-h4 u-els-font-family-bold',
};

// FR-1.10: a stale entry's reference markers stop being interactive and render as plain text —
// the `(1)`/`(2)` index text is unchanged, only the `Button` wrapper is dropped.
export function RefLink({ children }) {
  const { refsFromSummary, setOpenedReference, isStale } = useContext(RefLinkContext);
  const cnr = Array.isArray(children) ? children.join('') : children;
  const index = refsFromSummary[cnr];

  if (index === undefined) {
    return cnr;
  }

  if (isStale) {
    return `(${index})`;
  }

  return (
    <Button
      type="link"
      data-e2e="summary-ref"
      linkOptions={['emphasized']}
      aria-label={`View reference ${index}`}
      onClick={() => setOpenedReference(cnr)}>
      ({index})
    </Button>
  );
}

const sanitizeSchema = {
  ...defaultSchema,
  tagNames: [...(defaultSchema.tagNames || []), REF_LINK_TAG_NAME],
  protocols: {
    ...defaultSchema.protocols,
    href: ['http', 'https', 'mailto'],
  },
};

const sanitizeMarkdownUrl = (url) => (url.startsWith('//') ? '' : defaultUrlTransform(url));

function ScrollableSummaryTable({ node: _node, ...tableProps }) {
  return (
    <div className="ai-search-summary__table-scroll rx-focusable">
      <table {...tableProps} aria-label="AI summary comparison table" />
    </div>
  );
}

ScrollableSummaryTable.propTypes = {
  node: PropTypes.object,
};

const markdownComponentsMap = {
  [REF_LINK_TAG_NAME]: RefLink,
  table: ScrollableSummaryTable,
  h1: 'h3',
  h2: 'h4',
  h3: 'h5',
};

const FEEDBACK_STATE_BY_SUBMITTED = {
  up: FEEDBACK_STATES.LIKED,
  down: FEEDBACK_STATES.DISLIKED,
};

function Summary({
  entry,
  limitToReferencedDocuments,
  limitToReferencedReactions,
  copySummaryToClipboard,
  submitSummaryFeedback,
  activeBreadcrumb,
  params,
  chainKey,
  isActive = false,
  isFirstSummary = false,
  showDisclosure = true,
  onLearnHowItWorks,
  onBeforeReferenceNavigation,
}) {
  const { summaryResponse } = entry;
  const { summary } = summaryResponse;

  const referencedCitations = useMemo(
    () => (Array.isArray(summaryResponse?.referencedCitations) ? summaryResponse.referencedCitations : []),
    [summaryResponse?.referencedCitations]
  );
  const referencedReactions = useMemo(
    () => (Array.isArray(summaryResponse?.referencedReactions) ? summaryResponse.referencedReactions : []),
    [summaryResponse?.referencedReactions]
  );

  // AR-1.2: derive from the completed response, never from an eligibility flag. `context`
  // remains authoritative for valid partial/no-answer reaction responses with no references.
  const isReactionSummary = summaryResponse?.context === reactions || !isEmpty(referencedReactions);
  const trackingContext = activeBreadcrumb?.context ?? params?.context;
  const referencedReactionIds = useMemo(() => referencedReactions.map((reaction) => reaction?.id).filter(Boolean), [referencedReactions]);
  const referencedCitationIds = useMemo(() => referencedCitations.map((citation) => citation?.id).filter(Boolean), [referencedCitations]);
  const isReferencesButtonDisabled = isReactionSummary ? isEmpty(referencedReactionIds) : isEmpty(referencedCitationIds);
  const isStale = !isActive;

  // FR-8.8: disabled while already viewing this entry's own references-only destination —
  // index 0 is always the entry's generation resultName (SD-1); anything after it is derived.
  const isOwnReferencesDestination = entry.validForResultNames.indexOf(activeBreadcrumb?.resultName) > 0;

  // AR-3.5: reactions number distinct composite CITATIONS straight off the tokens in `summary`;
  // documents keep the existing exact-match-against-referencedCitations behaviour.
  const refsFromSummary = useMemo(
    () => (isReactionSummary ? extractCompositeRefsFromSummary(summary) : extractRefsFromSummary(summary, referencedCitations)),
    [summary, referencedCitations, isReactionSummary]
  );
  const [referenceNavigationError, setReferenceNavigationError] = useState(null);
  const navigateToReference = useCallback(
    (referenceToken) => {
      if (!isReactionSummary) {
        const referenceTarget = getResultReferenceTargetByResultId(referenceToken, RESULT_REFERENCE_TARGETS.DOCUMENT);
        if (!focusAndScrollReferenceTarget(referenceTarget)) {
          setReferenceNavigationError("We couldn't open the referenced result.");
          return;
        }

        setReferenceNavigationError(null);
        return;
      }

      const referencedReaction = findReactionForConditionToken(referencedReactions, referenceToken);
      if (!referencedReaction) {
        const reactionWithoutCondition = findReactionForIdToken(referencedReactions, referenceToken);
        if (!reactionWithoutCondition) {
          setReferenceNavigationError("We couldn't open the referenced condition.");
          return;
        }

        setReferenceNavigationError(null);
        revealReaction({
          resultName: activeBreadcrumb?.resultName,
          reactionId: reactionWithoutCondition.id,
        }).catch(() => setReferenceNavigationError("We couldn't open the referenced result."));
        return;
      }

      setReferenceNavigationError(null);
      revealReactionCondition({
        resultName: activeBreadcrumb?.resultName,
        reactionId: referencedReaction.id,
        conditionId: referenceToken,
      }).catch(() => setReferenceNavigationError("We couldn't open the referenced condition."));
    },
    [activeBreadcrumb?.resultName, isReactionSummary, referencedReactions]
  );

  const onReferenceClick = useCallback(
    (referenceToken) => {
      trackAssistantFeatureClick(ASSISTANT_FEATURES.REFERENCE_CLICK, trackingContext);
      if (!onBeforeReferenceNavigation) {
        navigateToReference(referenceToken);
        return;
      }

      onBeforeReferenceNavigation();
      const scheduleNavigation = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));
      scheduleNavigation(() => navigateToReference(referenceToken));
    },
    [navigateToReference, onBeforeReferenceNavigation, trackingContext]
  );

  const refLinkContextValue = useMemo(
    () => ({ refsFromSummary, setOpenedReference: onReferenceClick, isStale }),
    [refsFromSummary, isStale, onReferenceClick]
  );

  const onCopySummaryClick = () => {
    copySummaryToClipboard({ ...summaryResponse, isReactionSummary, context: trackingContext });
  };

  const onReferencesClick = () => {
    trackAssistantFeatureClick(ASSISTANT_FEATURES.VIEW_REFERENCED_ONLY, trackingContext);
    const { databaseId, sort, order } = params;
    if (isReactionSummary) {
      limitToReferencedReactions({ reactionIds: referencedReactionIds, databaseId, sort, order, chainKey, entryId: entry.id });
      return;
    }

    limitToReferencedDocuments({ cnrList: referencedCitationIds, databaseId, sort, order, chainKey, entryId: entry.id });
  };

  const onFeedbackChange = (liked) => {
    submitSummaryFeedback({ chainKey, entryId: entry.id, liked, context: trackingContext });
  };

  const feedbackState = FEEDBACK_STATE_BY_SUBMITTED[entry.feedbackSubmitted] ?? null;

  return (
    <div className={`ai-search-summary__content${isStale ? ' ai-search-summary__content--stale' : ''}`} data-testid="summary-content">
      <RefLinkContext.Provider value={refLinkContextValue}>
        <div className="s-els-non-authored-content ai-search-summary__body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema], [rehypeClassNames, mdClassNamesMapping]]}
            urlTransform={sanitizeMarkdownUrl}
            components={markdownComponentsMap}>
            {summary}
          </ReactMarkdown>
        </div>
      </RefLinkContext.Provider>
      {referenceNavigationError && (
        <p role="alert" className="u-els-color-n-error u-els-margin-top-1o2">
          {referenceNavigationError}
        </p>
      )}
      <div className="ai-search-summary__actions" data-testid="summary-actions">
        <div className="ai-search-summary__feedback">
          <ThumbsUpDown
            thumbsUpProps={{
              'data-e2e': 'summary-like',
              ...(isStale && { disabled: true }),
            }}
            thumbsDownProps={{
              'data-e2e': 'summary-dislike',
              ...(isStale && { disabled: true }),
            }}
            thumbsUpTooltip="Like"
            thumbsDownTooltip="Dislike"
            onFeedbackChange={onFeedbackChange}
            feedbackState={feedbackState}
            ariaLabel="AI Summary feedback"
            announceLike="You liked this summary."
            announceDislike="You disliked this summary."
          />
        </div>
        <span className="ai-search-summary__actions-splitbar" aria-hidden="true" />
        <div className="ai-search-summary__utility-actions">
          <SummaryActionTooltip label="Copy insights" onlyWhenLabelHidden>
            <ButtonWithIcon
              className="ai-search-summary__action"
              data-e2e="copy-summary-button"
              type="link"
              sprite="DuplicateDocument"
              iconRight
              disabled={isStale}
              onClick={onCopySummaryClick}>
              Copy insights
            </ButtonWithIcon>
          </SummaryActionTooltip>
          <SummaryActionTooltip
            label={isReactionSummary ? 'View referenced reactions only' : 'View referenced documents only'}
            onlyWhenLabelHidden>
            <ButtonWithIcon
              className="ai-search-summary__action"
              data-e2e="references-button"
              type="link"
              sprite="Recenter"
              iconRight
              disabled={isStale || isReferencesButtonDisabled || isOwnReferencesDestination}
              onClick={onReferencesClick}>
              {isReactionSummary ? 'View referenced reactions only' : 'View referenced documents only'}
            </ButtonWithIcon>
          </SummaryActionTooltip>
        </div>
      </div>
      {showDisclosure && (
        <SummaryDisclosureBanner
          isFirstSummary={isFirstSummary}
          isReactionSummary={isReactionSummary}
          onLearnHowItWorks={onLearnHowItWorks}
        />
      )}
    </div>
  );
}

export default Summary;
